import { useEffect, useState } from 'react';
import { api, type Organization } from '../api/endpoints';

/**
 * Eigene Organisationen für Kopfzeile (Umschalter) und Brotkrumen. Die Liste wird kurz zwischengespeichert,
 * damit nicht jede Seite neu fragt; Fehler bleiben still (die Kopfzeile funktioniert auch ohne).
 */
const MAX_AGE_MS = 60_000;
let cache: { at: number; promise: Promise<Organization[]> } | null = null;

export function loadOrganizations(): Promise<Organization[]> {
  const now = Date.now();
  if (cache !== null && now - cache.at < MAX_AGE_MS) return cache.promise;
  const promise = api.listOrganizations();
  cache = { at: now, promise };
  promise.catch(() => {
    if (cache?.promise === promise) cache = null;
  });
  return promise;
}

/** Zwischenspeicher leeren (nach dem Anlegen einer Organisation und in Tests). */
export function resetOrganizationsCache(): void {
  cache = null;
}

/** Wie `useOrganizations`, meldet zusätzlich, dass das Laden gescheitert ist (sonst bliebe „Wird geladen …“ stehen). */
export function useOrganizationsState(enabled = true): {
  orgs: Organization[] | null;
  failed: boolean;
} {
  const [state, setState] = useState<{ orgs: Organization[] | null; failed: boolean }>({
    orgs: null,
    failed: false,
  });
  useEffect(() => {
    if (!enabled) return;
    const guard = { cancelled: false };
    loadOrganizations().then(
      (loaded) => {
        if (!guard.cancelled) setState({ orgs: loaded, failed: false });
      },
      () => {
        if (!guard.cancelled) setState({ orgs: null, failed: true });
      },
    );
    return () => {
      guard.cancelled = true;
    };
  }, [enabled]);
  return state;
}

export function useOrganizations(enabled = true): Organization[] | null {
  return useOrganizationsState(enabled).orgs;
}
